import { useEffect, useMemo, useState } from "react";
import { IProduct, TYPE_MEASUREDUNIT } from "../../../../types/product.types";
import { getProductById, updateProduct } from "../../../../common/services/product.api";
import { useToastContext } from "../../../../common/components/ui/toast/toast.context";

const MEASURED_UNITS = ["CJ", "KIT", "UNID"] as const;

type Props = {
  productId: string;
  onClose: () => void;
  onSave: () => void;
};

export default function FormUpdateProduct({ productId, onSave }: Props) {
  const { notifyError, notifySuccess } = useToastContext();
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState<Partial<IProduct>>({});

  useEffect(() => {
    async function load() {
      setLoading(true);
      const result = await getProductById(productId);
      if (!result.ok) {
        notifyError(result.message || "Erro ao carregar produto");
        setLoading(false);
        return;
      }
      setForm(result.data);
      setLoading(false);
    }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId]);

  const isValid = useMemo(() => {
    return (
      (form.code?.trim()?.length ?? 0) > 0 &&
      (form.name?.trim()?.length ?? 0) > 0 &&
      (form.category?.trim()?.length ?? 0) > 0 &&
      !Number.isNaN(form.maximumApplication) &&
      !Number.isNaN(form.durability) &&
      !Number.isNaN(form.qtdFrac)
    );
  }, [form]);

  function handleChange<K extends keyof IProduct>(
    key: K,
    value: IProduct[K]
  ) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    try {
      const dataToSend: Partial<IProduct> = {
        ...form,
        maximumApplication: Number(form.maximumApplication),
        durability: Number(form.durability),
        qtdFrac: Number(form.qtdFrac),
      };
      const resp = await updateProduct(productId, dataToSend);
      if (!resp.ok) {
        throw new Error(resp.message || "Falha ao atualizar produto");
      }
      notifySuccess("Produto atualizado com sucesso!");
      onSave();
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      notifyError(err?.message || "Falha ao atualizar produto");
    } finally {
      setLoading(false);
    }
  }

  const inputCls =
    "px-3 py-2 border border-gray-300 rounded-lg bg-white outline-none focus:ring-2 focus:ring-gray-900/20 focus:border-gray-400";

  if (loading) return <p>Carregando...</p>;

  return (
    <div>
      <h1 className="text-2xl font-semibold mb-4">Editar informações do produto</h1>
      <div>
        <div>
          <form onSubmit={handleSubmit} className="flex flex-col gap-3">
            <div className="flex w-full gap-4">
              <div className="space-y-1 flex flex-col w-full">
                <label htmlFor="code">Código *</label>
                <input
                  id="code"
                  placeholder="Digite o código"
                  value={form.code ?? ""}
                  onChange={(e) => handleChange("code", e.target.value)}
                  required
                  className={inputCls}
                />
              </div>
              <div className="space-y-1 flex flex-col w-full">
                <label htmlFor="category">Categoria *</label>
                <input
                  id="category"
                  placeholder="Freios"
                  value={form.category ?? ""}
                  onChange={(e) => handleChange("category", e.target.value)}
                  required
                  className={inputCls}
                />
              </div>
              <div className="space-y-1 flex flex-col w-full">
                <label htmlFor="name">Nome *</label>
                <input
                  id="name"
                  placeholder="Pastilha de Freio"
                  value={form.name ?? ""}
                  onChange={(e) => handleChange("name", e.target.value)}
                  required
                  className={inputCls}
                />
              </div>
            </div>
            <div className="space-y-1 flex flex-col">
              <label htmlFor="brand">Marca *</label>
              <input
                id="brand"
                placeholder="Brembo"
                value={form.brand ?? ""}
                onChange={(e) => handleChange("brand", e.target.value)}
                className={inputCls}
              />
            </div>
            <div className="space-y-1 flex flex-col">
              <label htmlFor="maximumApplication">Aplicação Máxima *</label>
              <input
                id="maximumApplication"
                type="number"
                min={0}
                value={form.maximumApplication === 0 || form.maximumApplication === undefined ? "" : form.maximumApplication}
                onChange={(e) => handleChange("maximumApplication", Number(e.target.value))}
                required
                className={inputCls}
              />
            </div>
            <div className="flex w-full gap-4">
              <div className="space-y-1 flex flex-col w-full">
                <label htmlFor="durability">Durabilidade (meses) *</label>
                <input
                  id="durability"
                  type="number"
                  min={0}
                  value={form.durability === 0 || form.durability === undefined ? "" : form.durability}
                  onChange={(e) => handleChange("durability", Number(e.target.value))}
                  required
                  className={inputCls}
                />
              </div>
              <div className="space-y-1 flex flex-col w-full">
                <label htmlFor="qtdFrac">Qtd. Fracionada</label>
                <input
                  id="qtdFrac"
                  type="number"
                  min={0}
                  value={form.qtdFrac === 0 || form.qtdFrac === undefined ? "" : form.qtdFrac}
                  onChange={(e) => handleChange("qtdFrac", Number(e.target.value))}
                  className={inputCls}
                />
              </div>
              <div className="space-y-1 flex flex-col w-full">
                <label htmlFor="measuredunit">Unidade de Medida</label>
                <select
                  id="measuredunit"
                  value={form.measuredunit ?? ""}
                  onChange={(e) => handleChange("measuredunit", e.target.value as TYPE_MEASUREDUNIT)}
                  required
                  className={inputCls}
                >
                  {MEASURED_UNITS.map((u) => (
                    <option key={u} value={u}>
                      {u}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="flex items-center justify-between p-3 border border-gray-200 rounded-xl md:col-span-1 w-full">
                <div>
                  <label htmlFor="preventive" className="cursor-pointer">
                    Preventivo
                  </label>
                  <div className="text-xs text-gray-500">
                    Indicador para manutenções preventivas
                  </div>
                </div>
                <input
                  id="preventive"
                  type="checkbox"
                  checked={form.preventive ?? false}
                  onChange={(e) => handleChange("preventive", e.target.checked)}
                />
              </div>
              <div className="flex items-center justify-between p-3 border border-gray-200 rounded-xl md:col-span-1 w-full">
                <div>
                  <label htmlFor="corrective" className="cursor-pointer">
                    Corretivo
                  </label>
                  <div className="text-xs text-gray-500">
                    Indicador para manutenções corretivas
                  </div>
                </div>
                <input
                  id="corrective"
                  type="checkbox"
                  checked={form.corrective ?? false}
                  onChange={(e) => handleChange("corrective", e.target.checked)}
                />
              </div>
              <div className="flex items-center justify-between p-3 border border-gray-200 rounded-xl md:col-span-2 w-full">
                <div>
                  <label htmlFor="misuse" className="cursor-pointer">
                    Mau uso
                  </label>
                  <div className="text-xs text-gray-500">Marcar quando aplicável</div>
                </div>
                <input
                  id="misuse"
                  type="checkbox"
                  checked={form.misuse ?? false}
                  onChange={(e) => handleChange("misuse", e.target.checked)}
                />
              </div>
            </div>
            <div className="flex flex-col gap-4 md:flex-row">
              <div className="space-y-1 flex flex-col w-full">
                <label htmlFor="durability">NCM *</label>
                <input
                  id="ncm"
                  type="text"
                  value={form.ncm}
                  onChange={(e) => handleChange("ncm", e.target.value)}
                  required
                  className={inputCls}
                />
              </div>
              <div className="space-y-1 flex flex-col w-full">
                <label htmlFor="durability">CST *</label>
                <input
                  id="cst"
                  type="text"
                  value={form.cst}
                  onChange={(e) => handleChange("cst", e.target.value)}
                  required
                  className={inputCls}
                />
              </div>
            </div>
            <div className="flex gap-3 md:col-span-2 justify-end">
              <button
                type="submit"
                disabled={!isValid || loading}
                className="px-4 py-2 rounded-lg bg-locavibe text-white hover:bg-red-700"
              >
                {loading ? "Salvando..." : "Salvar"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
